import React, { useState } from "react";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  Mail,
  Phone,
  MapPin,
  Building2,
  MessageSquare,
  User,
  Send,
  Globe,
} from "lucide-react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // FIX: Double slash hataya aur headers add kiye
      const response = await api.post(
        "/contact",
        formData
      );

      if (response.data?.success) {
        toast.success("Message Sent Successfully! 🚀");
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
        });
      } else {
        toast.error(response.data?.message || "Failed to send message.");
      }
    } catch (error) {
      console.error("Contact Form Error:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Server Error! Please try again later.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact-form"
      className="bg-gray-50 dark:bg-background py-20 overflow-hidden transition-colors duration-200"
    >
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-2 gap-12 items-center transition-colors duration-200">
          {/* LEFT SIDE: Text & Details */}
          <div>
            <div className="inline-block px-4 py-1 rounded-full bg-yellow-100 text-[#d9b300] font-bold text-sm tracking-widest uppercase mb-6">
              Contact Us
            </div>

            <h2 className="text-5xl font-extrabold text-gray-900 dark:text-white leading-tight">
              Let's Talk About <br />
              <span className="text-[#FED547]">Your Growth</span> 🚀
            </h2>

            <p className="mt-6 text-gray-600 dark:text-gray-300 leading-8 text-lg">
              Looking for hiring solutions or your next career opportunity? Fill
              out the form and our team will get back to you shortly.
            </p>

            <div className="mt-10 space-y-8">
              {[
                { icon: Mail, title: "Email", value: "hr@kadtechhr.com" },
                { icon: Phone, title: "Phone", value: "+91 72596 79676" },
                { icon: Globe, title: "Website", value: "www.kadtechhr.com" },
                {
                  icon: MapPin,
                  title: "Office",
                  value: "Bangalore, Karnataka, India",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-5 group">
                  <div className="w-14 h-14 rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-800 flex items-center justify-center group-hover:bg-[#FED547] transition-all duration-300">
                    <item.icon
                      className="text-[#FED547] group-hover:text-white transition-colors"
                      size={24}
                    />
                  </div>

                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">
                      {item.title}
                    </p>

                    <p className="text-gray-500 dark:text-gray-300">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE: Form */}
          <div className="bg-white dark:bg-gray-800 rounded-[2rem] shadow-2xl p-8 md:p-12 border border-gray-100 dark:border-gray-800 transition-colors duration-200">
            <h3 className="text-2xl font-bold mb-8 text-gray-900 dark:text-white">
              Send Us A Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div className="relative group">
                  <User
                    className="absolute left-4 top-4 text-gray-400 dark:text-gray-500 group-focus-within:text-[#FED547]"
                    size={18}
                  />

                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 h-14 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none transition-all px-4"
                  />
                </div>

                <div className="relative group">
                  <Mail
                    className="absolute left-4 top-4 text-gray-400 dark:text-gray-500 group-focus-within:text-[#FED547]"
                    size={18}
                  />

                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 h-14 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none transition-all px-4"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full h-14 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none px-4 transition-all"
                />

                <div className="relative group">
                  <Building2
                    className="absolute left-4 top-4 text-gray-400 dark:text-gray-500 group-focus-within:text-[#FED547]"
                    size={18}
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full pl-12 h-14 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none px-4 transition-all"
                  />
                </div>
              </div>

              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full h-14 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none px-4 transition-all"
              />

              <div className="relative group">
                <MessageSquare
                  className="absolute left-4 top-5 text-gray-400 dark:text-gray-500 group-focus-within:text-[#FED547]"
                  size={18}
                />
                <textarea
                  rows="5"
                  name="message"
                  placeholder="Write your message..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pt-4 rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-white focus:border-[#FED547] focus:ring-2 focus:ring-[#FED547]/20 outline-none resize-none transition-all px-4"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-xl bg-[#FED547] hover:bg-black text-black hover:text-white font-bold transition-all duration-300 flex items-center justify-center gap-2 transform active:scale-95 shadow-lg shadow-yellow-500/20"
              >
                {loading ? (
                  "Sending..."
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
